import { useState, useCallback } from 'react'
import {
  prepareEstimateFn,
  refreshEstimateFn,
  sendEstimateToClientFn,
  sendDepositInvoiceFn,
  sendFinalInvoiceFn,
} from '@/lib/firebase/functions'

export type BillingAction =
  | 'prepare_estimate'
  | 'send_estimate'
  | 'send_deposit_invoice'
  | 'send_final_invoice'

export interface BillingActionFeedback {
  action: BillingAction
  kind: 'success' | 'warning' | 'error'
  title: string
  message: string
  detail?: string
}

interface UseEngagementInvoicingReturn {
  loading: boolean
  loadingAction: BillingAction | null
  feedback: BillingActionFeedback | null
  dismissFeedback: () => void
  createEstimate: () => Promise<void>
  updateEstimate: () => Promise<void>
  sendEstimateToClient: () => Promise<void>
  sendDepositInvoice: () => Promise<void>
  sendFinalInvoice: () => Promise<void>
}

interface CallableErrorLike {
  code?: unknown
  message?: unknown
}

interface SendEstimateResult {
  success: boolean
  expiresAt: number
  recipient?: string
  messageId?: string
  remindersScheduled?: boolean
}

function callableErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null
  const code = (error as CallableErrorLike).code
  return typeof code === 'string' ? code : null
}

function callableErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message
  if (error && typeof error === 'object') {
    const message = (error as CallableErrorLike).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return 'An unexpected error occurred.'
}

const ACTION_ERROR_TITLES: Record<BillingAction, string> = {
  prepare_estimate: 'Estimate preparation failed',
  send_estimate: 'Estimate email failed',
  send_deposit_invoice: 'Deposit invoice failed',
  send_final_invoice: 'Final invoice failed',
}

export function useEngagementInvoicing(
  engagementId: string,
  onSuccess: () => void
): UseEngagementInvoicingReturn {
  const [loadingAction, setLoadingAction] = useState<BillingAction | null>(null)
  const [feedback, setFeedback] = useState<BillingActionFeedback | null>(null)

  const run = useCallback(
    async <T,>(
      action: BillingAction,
      fn: () => Promise<T>,
      successFeedback: (result: T) => BillingActionFeedback
    ) => {
      setLoadingAction(action)
      setFeedback(null)
      try {
        const result = await fn()
        setFeedback(successFeedback(result))
        onSuccess()
      } catch (err) {
        const message = callableErrorMessage(err)
        const code = callableErrorCode(err)
        setFeedback({
          action,
          kind: 'error',
          title: ACTION_ERROR_TITLES[action],
          message,
          ...(code ? { detail: code } : {}),
        })
      } finally {
        setLoadingAction(null)
      }
    },
    [onSuccess]
  )

  const createEstimate = useCallback(
    () => run(
      'prepare_estimate',
      () => prepareEstimateFn({ engagementId }),
      () => ({
        action: 'prepare_estimate',
        kind: 'success',
        title: 'Estimate prepared',
        message: 'The estimate is ready for review and can now be sent to the client.',
      })
    ),
    [engagementId, run]
  )

  const updateEstimate = useCallback(
    () => run(
      'prepare_estimate',
      () => refreshEstimateFn({ engagementId }),
      () => ({
        action: 'prepare_estimate',
        kind: 'success',
        title: 'Estimate refreshed',
        message: 'The saved estimate now reflects the current engagement details.',
      })
    ),
    [engagementId, run]
  )

  const sendEstimateToClient = useCallback(
    () => run<SendEstimateResult>(
      'send_estimate',
      async () => (await sendEstimateToClientFn({ engagementId })).data,
      (result) => {
        const remindersScheduled = result.remindersScheduled !== false
        const recipient = result.recipient ?? 'the client'
        return {
          action: 'send_estimate',
          kind: remindersScheduled ? 'success' : 'warning',
          title: remindersScheduled
            ? 'Estimate email accepted for delivery'
            : 'Estimate sent; reminders need attention',
          message: remindersScheduled
            ? `The estimate was accepted for delivery to ${recipient}.`
            : `The estimate was accepted for delivery to ${recipient}, but its reminder or expiration task could not be scheduled.`,
          ...(result.messageId ? { detail: `Provider message ID: ${result.messageId}` } : {}),
        }
      }
    ),
    [engagementId, run]
  )

  const sendDepositInvoice = useCallback(
    () => run(
      'send_deposit_invoice',
      () => sendDepositInvoiceFn({ engagementId }),
      () => ({
        action: 'send_deposit_invoice',
        kind: 'success',
        title: 'Deposit invoice sent',
        message: 'Stripe accepted the deposit invoice for delivery to the client.',
      })
    ),
    [engagementId, run]
  )

  const sendFinalInvoice = useCallback(
    () => run(
      'send_final_invoice',
      () => sendFinalInvoiceFn({ engagementId }),
      () => ({
        action: 'send_final_invoice',
        kind: 'success',
        title: 'Final invoice sent',
        message: 'Stripe accepted the final invoice for delivery to the client.',
      })
    ),
    [engagementId, run]
  )

  const dismissFeedback = useCallback(() => setFeedback(null), [])

  return {
    loading: loadingAction !== null,
    loadingAction,
    feedback,
    dismissFeedback,
    createEstimate, updateEstimate, sendEstimateToClient,
    sendDepositInvoice, sendFinalInvoice,
  }
}
